import { VerificationNames, VerificationStatus } from "shared/types";
import { Verification } from "../types";
import {
  AlertCircle,
  Check,
  Loading02,
  X,
  HelpCircle
} from "untitledui-js-base";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "shared/components/Tooltip";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";

export const VerificationBadge = ({
  verification
}: {
  verification: Verification;
}) => {
  const { unit } = useUnitsPropertiesDictionary();
  switch (verification.status) {
    case VerificationStatus.Verified:
    case VerificationStatus.ManuallyApproved:
    case VerificationStatus.Finished:
      return (
        <div className="flex items-center gap-2">
          <Check className="stroke-success-600 h-4 w-4 shrink-0" />
          <span className="text-xs text-success-700">
            {verification.name === VerificationNames.IdentityVerification
              ? "Passed"
              : "Completed"}
          </span>
        </div>
      );

    case VerificationStatus.FinishedWithWarnings:
      return (
        <div className="flex items-center gap-2">
          <Check className="stroke-warning-600 h-4 w-4 shrink-0" />
          <span className="text-xs text-warning-700">
            {verification.name === VerificationNames.IdentityVerification
              ? "Passed"
              : "Completed"}{" "}
            with warnings
          </span>
        </div>
      );

    case VerificationStatus.Failed:
      if (verification.name === VerificationNames.IdentityVerification)
        return (
          <div className="flex items-center gap-2">
            <X className="stroke-danger-600 h-4 w-4 shrink-0" />
            <span className="text-xs text-danger-700">Fail</span>
          </div>
        );

      return (
        <div className="flex items-center gap-2">
          <AlertCircle className="stroke-danger-600 h-4 w-4 shrink-0" />
          <span className="text-xs text-danger-700">Issues</span>
        </div>
      );

    case VerificationStatus.Processing:
    case VerificationStatus.Started:
    case VerificationStatus.Submitted:
      return (
        <div className="flex items-center gap-2">
          <Loading02 className="stroke-warning-500 h-4 w-4 shrink-0" />
          <span className="text-xs font-medium text-content-400">
            In progress
          </span>
        </div>
      );

    case VerificationStatus.Pending:
      return (
        <div className="flex items-center gap-2">
          <div className="rounded-full h-4 w-4 shrink-0 border border-dashed border-content-100"></div>
          <span className="text-xs font-medium text-content-400">
            Not started
          </span>
        </div>
      );

    case VerificationStatus.Canceled:
      return (
        <div className="flex items-center gap-2">
          <X className="stroke-content-100 h-4 w-4 shrink-0" />
          <span className="text-xs text-content-400">Canceled</span>
        </div>
      );

    case VerificationStatus.NoDataAvailable:
      return (
        <Tooltip placement="bottom">
          <TooltipTrigger as="div" className="flex items-center gap-2">
            <HelpCircle className="stroke-danger-500 h-4 w-4 shrink-0" />
            <span className="text-xs text-content-400">No data available</span>
          </TooltipTrigger>
          <TooltipContent className="max-w-[370px] rounded-lg border bg-alpha-800 p-4 text-sm text-white z-[50]">
            No data available. Application was moved from a {unit} where this
            data was not requested
          </TooltipContent>
        </Tooltip>
      );
  }
};
